import React from 'react';
import AudioPlayerContent from './AudioPlayerContent';
import ChecklistContent from './ChecklistContent';
import EnergySummaryContent from './EnergySummaryContent';
import FindLocationContent from './FindLocationContent';
import HRSummaryContent from './HRSummaryContent';
import JournalPromptsContent from './JournalPromptsContent';
import MealDetailsContent from './MealDetailsContent';
import MealPlanWeekContent from './MealPlanWeekContent';
import MealSuggestionContent from './MealSuggestionContent';
import MetricBarContent from './MetricBarContent';
import SleepSummaryContent from './SleepSummaryContent';
import TransitRouteContent from './TransitRouteContent';
import WorkoutPlanCard from './WorkoutPlanCard';
import type { RichContextCardCTA } from './WorkoutPlanCard';
import WorkoutSummaryCard from './WorkoutSummaryCard';
import {
  CONTENT_ICON,
  isFindLocationLike,
  isHRSummaryLike,
  normalizeRichContentType,
  parseWorkoutSummaryPayload,
  RichPayload,
} from './contentCardUtils';
import './richContext.scss';
import { useTranslation } from 'react-i18next';

interface ContentCardProps {
  type: string;
  payload: RichPayload;
  enrichmentPending: boolean;
  enrichmentStatusText?: string | null;
  onEditMealContent?: () => void;
  ctas?: RichContextCardCTA[];
  hideHeader?: boolean;
  className?: string;
  /** When true, meal details body uses a horizontal row layout (e.g. assistant chat rich context). */
  fromAssistantRichContextCard?: boolean;
}

const ContentCard: React.FC<ContentCardProps> = ({
  type,
  payload,
  enrichmentPending,
  enrichmentStatusText,
  onEditMealContent,
  ctas,
  hideHeader = false,
  className,
  fromAssistantRichContextCard = false,
}) => {
  const { t } = useTranslation();
  const p = payload as Record<string, unknown>;
  const normalizedType = normalizeRichContentType(type);
  const icon = CONTENT_ICON[type] ?? CONTENT_ICON[normalizedType] ?? '•';
  const displayTitle =
    normalizedType === 'find_location'
      ? (((p.query || p.label || p.title || p.name) as string | undefined) ?? 'place')
      : normalizedType === 'meal_suggestion'
        ? t('task.richContext.mealSuggestion')
        : normalizedType === 'meal_content'
          ? t('task.richContext.pastMealDetails')
          : normalizedType === 'sleep_summary'
            ? t('task.richContext.sleepSuggestion')
            : normalizedType === 'workout_summary'
              ? (typeof p.workout_type === 'string' && p.workout_type.trim()) ||
                t('task.richContext.workout')
              : normalizedType === 'workout_plan'
                ? (typeof p.plan_title === 'string' && p.plan_title.trim()) ||
                  t('task.richContext.workout')
                : normalizedType.replace(/_/g, ' ');

  const renderBody = (): React.ReactNode => {
    switch (normalizedType) {
      case 'workout_plan':
        return 'plan_title' in p || enrichmentPending ? (
          <WorkoutPlanCard
            p={p as Parameters<typeof WorkoutPlanCard>[0]['p']}
            enrichmentPending={enrichmentPending}
            ctas={ctas}
          />
        ) : null;
      case 'workout_summary':
        return <WorkoutSummaryCard p={parseWorkoutSummaryPayload(p)} />;
      case 'checklist':
        return 'items' in p ? (
          <ChecklistContent p={p as Parameters<typeof ChecklistContent>[0]['p']} />
        ) : null;
      case 'meal_suggestion':
        return (
          <MealSuggestionContent
            p={
              'meal_content' in p
                ? (p as Parameters<typeof MealSuggestionContent>[0]['p'])
                : ({ ...p, meal_content: p } as Parameters<typeof MealSuggestionContent>[0]['p'])
            }
            enrichmentPending={enrichmentPending}
            onEditMealContent={onEditMealContent}
          />
        );
      case 'meal_content':
        if ('meal_content' in p && p.meal_content == null) return null;
        return 'meal_details' in p ||
          'food_items' in p ||
          'foodItems' in p ||
          'start_time' in p ||
          'startTime' in p ||
          'end_time' in p ||
          'endTime' in p ||
          'type' in p ||
          'title' in p ||
          'dish_name' in p ||
          'nearby_places' in p ||
          'dish_tags' in p ||
          'venue_name' in p ||
          'macros' in p ||
          'micros' in p ||
          'rating' in p ? (
          <MealDetailsContent
            p={p}
            enrichmentPending={enrichmentPending}
            fromAssistantRichContextCard={fromAssistantRichContextCard}
          />
        ) : null;
      case 'find_location':
        return isFindLocationLike(p) ? (
          <FindLocationContent p={p} enrichmentPending={enrichmentPending} />
        ) : null;
      case 'transit_route':
        return 'origin' in p ? (
          <TransitRouteContent p={p as Parameters<typeof TransitRouteContent>[0]['p']} />
        ) : null;
      case 'energy':
        return 'current_zone' in p ||
          'current_window' in p ||
          'next_zone' in p ||
          'zones' in p ||
          'minutes_to_next_zone' in p ? (
          <EnergySummaryContent p={p as Parameters<typeof EnergySummaryContent>[0]['p']} />
        ) : null;
      case 'sleep_summary':
        return <SleepSummaryContent p={p as Parameters<typeof SleepSummaryContent>[0]['p']} />;
      case 'hr_summary':
        return isHRSummaryLike(p) ? (
          <HRSummaryContent p={p as Parameters<typeof HRSummaryContent>[0]['p']} />
        ) : null;
      case 'metric_bar':
        return 'value' in p && 'unit' in p ? (
          <MetricBarContent p={p as Parameters<typeof MetricBarContent>[0]['p']} />
        ) : null;
      case 'audio_player':
        return 'track_name' in p ? (
          <AudioPlayerContent p={p as Parameters<typeof AudioPlayerContent>[0]['p']} />
        ) : null;
      case 'journal_prompts':
        return 'prompts' in p ? (
          <JournalPromptsContent p={p as Parameters<typeof JournalPromptsContent>[0]['p']} />
        ) : null;
      case 'meal_plan_week':
        return 'days' in p ? (
          <MealPlanWeekContent p={p as Parameters<typeof MealPlanWeekContent>[0]['p']} />
        ) : null;
      default:
        return (
          <div className="rc-content-body">
            <span className="rc-content-meta">{t('task.richContext.unsupportedContentType')}</span>
          </div>
        );
    }
  };

  const body = renderBody();
  if (!body && !enrichmentPending) return null;

  return (
    <div className={`rc-content-card${className ? ` ${className}` : ''}`}>
      {!hideHeader && (
        <div className="rc-content-header">
          <span className="rc-content-icon">{icon}</span>
          <span className="rc-content-type">{displayTitle}</span>
          {normalizedType === 'meal_content' && !!onEditMealContent && (
            <button className="rc-header-action-button" onClick={onEditMealContent} type="button">
              <span className="rc-header-action-text">{t('task.common.edit')}</span>
            </button>
          )}
          {enrichmentPending && (
            <div className="rc-updating-badge">
              <span className="rc-updating-text">
                {enrichmentStatusText?.trim()
                  ? enrichmentStatusText
                  : t('task.richContext.updating')}
              </span>
            </div>
          )}
        </div>
      )}
      {body ?? (
        <div className="rc-skeleton-group">
          <div className="rc-skeleton-row" />
          <div className="rc-skeleton-row rc-skeleton-row--short" />
        </div>
      )}
    </div>
  );
};

export default ContentCard;
